import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useSignalStore } from '@/page/token/signal/store';
import { useAppStore } from '@/store';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useInput } from '../hook/useInput';
import { useMemo } from 'react';

interface IProps {
  setOpen: (open: boolean) => void;
}
export default (props: IProps) => {
  const { setOpen } = props;
  const { fwClass } = useAppStore();
  const { t } = useTranslation();
  const { filterConfig, setFilterConfig } = useSignalStore();

  const alertTimesStart = useInput(filterConfig.alertTimes.start, { onlyNumber: true, onlyInt: true });
  const alertTimesEnd = useInput(filterConfig.alertTimes.end, { onlyNumber: true, onlyInt: true });

  const mcStart = useInput(filterConfig.mc.start, { onlyNumber: true, onlyInt: true });
  const mcEnd = useInput(filterConfig.mc.end, { onlyNumber: true, onlyInt: true });

  const liquidityStart = useInput(filterConfig.liquidity.start, { onlyNumber: true, onlyInt: true });
  const liquidityEnd = useInput(filterConfig.liquidity.end, { onlyNumber: true, onlyInt: true });

  const holdersStart = useInput(filterConfig.holders.start, { onlyNumber: true, onlyInt: true });
  const holdersEnd = useInput(filterConfig.holders.end, { onlyNumber: true, onlyInt: true });

  const onReset = () => {
    alertTimesStart.setValue('');
    alertTimesEnd.setValue('');
    mcStart.setValue('');
    mcEnd.setValue('');
    liquidityStart.setValue('');
    liquidityEnd.setValue('');
    holdersStart.setValue('');
    holdersEnd.setValue('');

    setFilterConfig({
      alertTimes: { start: '', end: '' },
      mc: { start: '', end: '' },
      liquidity: { start: '', end: '' },
      holders: { start: '', end: '' },
    });
    setOpen(false);
  };

  const onConfirm = () => {
    setFilterConfig({
      alertTimes: { start: alertTimesStart.value, end: alertTimesEnd.value },
      mc: { start: mcStart.value, end: mcEnd.value },
      liquidity: { start: liquidityStart.value, end: liquidityEnd.value },
      holders: { start: holdersStart.value, end: holdersEnd.value },
    });

    setOpen(false);
  };

  const checkAlertTimeStartState = useMemo(() => {
    return alertTimesStart.value !== '' && Number(alertTimesStart.value) == 0;
  }, [alertTimesStart.value]);

  const checkMcStartState = useMemo(() => {
    return mcStart.value !== '' && Number(mcStart.value) == 0;
  }, [mcStart.value]);

  const checkLiquidityStartState = useMemo(() => {
    return liquidityStart.value !== '' && Number(liquidityStart.value) == 0;
  }, [liquidityStart.value]);

  const checkHolderStartState = useMemo(() => {
    return holdersStart.value !== '' && Number(holdersStart.value) == 0;
  }, [holdersStart.value]);

  const checkAlertTimesState = useMemo(() => {
    return alertTimesStart.value !== '' && alertTimesEnd.value !== '' && Number(alertTimesStart.value) >= Number(alertTimesEnd.value);
  }, [alertTimesStart.value, alertTimesEnd.value]);

  const checkMcState = useMemo(() => {
    return mcStart.value !== '' && mcEnd.value !== '' && Number(mcStart.value) >= Number(mcEnd.value);
  }, [mcStart.value, mcEnd.value]);

  const checkLiquidityState = useMemo(() => {
    return liquidityStart.value !== '' && liquidityEnd.value !== '' && Number(liquidityStart.value) >= Number(liquidityEnd.value);
  }, [liquidityStart.value, liquidityEnd.value]);

  const checkHolderState = useMemo(() => {
    return holdersStart.value !== '' && holdersEnd.value !== '' && Number(holdersStart.value) >= Number(holdersEnd.value);
  }, [holdersStart.value, holdersEnd.value]);

  const disabled =
    checkAlertTimesState ||
    checkMcState ||
    checkLiquidityState ||
    checkHolderState ||
    checkAlertTimeStartState ||
    checkMcStartState ||
    checkLiquidityStartState ||
    checkHolderStartState;

  return (
    <div className="h-[500px] flex flex-col justify-between">
      <div className="flex flex-col gap-y-[20px]">
        <div className="flex justify-between">
          <span className={cn('text-[24px]', fwClass)}>{t('Signal.signalFilter')}</span>
          <X className="size-[16px]" onClick={() => setOpen(false)} />
        </div>
        <div className="flex justify-between items-center text-[12px]">
          <span>{t('Signal.signalAlertTimes')}</span>
          <div className="flex items-center gap-x-[12px]">
            <Input
              value={alertTimesStart.value}
              onChange={alertTimesStart.onChange}
              className={cn(
                'w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
                checkAlertTimeStartState ? '!border-sellColor' : '',
              )}
            />
            to
            <Input
              value={alertTimesEnd.value}
              onChange={alertTimesEnd.onChange}
              className={cn(
                'w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
                checkAlertTimesState ? '!border-sellColor' : '',
              )}
            />
          </div>
        </div>
        <div className="flex justify-between items-center text-[12px]">
          <span>{t('Signal.mc')}</span>
          <div className="flex items-center gap-x-[12px]">
            <div
              className={cn(
                'flex items-center pr-[12px] w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
              )}
            >
              <Input
                value={mcStart.value}
                onChange={mcStart.onChange}
                className={t('border-none outline-none shadow-none', checkMcStartState ? '!border-sellColor' : '')}
              />
              <span className="text-[14px]">K</span>
            </div>
            to
            <div
              className={cn(
                'flex items-center pr-[12px] w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
                checkMcState ? '!border-sellColor' : '',
              )}
            >
              <Input value={mcEnd.value} onChange={mcEnd.onChange} className="border-none outline-none shadow-none" />
              <span className="text-[14px]">K</span>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center text-[12px]">
          <span>{t('Signal.liquidity')}</span>
          <div className="flex items-center gap-x-[12px]">
            <div className="flex items-center pr-[12px] w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]">
              <Input
                value={liquidityStart.value}
                onChange={liquidityStart.onChange}
                className={t('border-none outline-none shadow-none', checkLiquidityStartState ? '!border-sellColor' : '')}
              />
              <span className="text-[14px]">K</span>
            </div>
            to
            <div
              className={cn(
                'flex items-center pr-[12px] w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
                checkLiquidityState ? '!border-sellColor' : '',
              )}
            >
              <Input value={liquidityEnd.value} onChange={liquidityEnd.onChange} className="border-none outline-none shadow-none" />
              <span className="text-[14px]">K</span>
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center text-[12px]">
          <span>{t('Signal.holders')}</span>
          <div className="flex items-center gap-x-[12px]">
            <Input
              value={holdersStart.value}
              onChange={holdersStart.onChange}
              className={cn(
                'w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
                checkHolderStartState ? '!border-sellColor' : '',
              )}
            />
            to
            <Input
              value={holdersEnd.value}
              onChange={holdersEnd.onChange}
              className={cn(
                'w-[82px] h-[36px] text-[14px] border-[1px] border-borderColor rounded-[8px] bg-[#F4F4F5] dark:bg-[#27272A]',
                checkHolderState ? '!border-sellColor' : '',
              )}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-between gap-x-[10px]">
        <Button variant="secondary" className={cn('w-full rounded-[12px] h-[48px] text-[14px]', fwClass)} onClick={onReset}>
          {t('Signal.reset')}
        </Button>
        <Button
          className={cn('!bg-themeColor w-full rounded-[12px] h-[48px] text-[14px] text-black', fwClass)}
          disabled={disabled}
          onClick={onConfirm}
        >
          {t('Signal.apply')}
        </Button>
      </div>
    </div>
  );
};
